import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Paper, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CategoriasTable from './CategoriasTable';
import CategoriasForm from './CategoriasForm';
import DeleteCategoriasDialog from './DeleteCategoriasDialog';
import {
    fetchCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria,
} from '../../services/empleados.api';
import { useSnackbar } from 'notistack';
import Navbar from "../../../../../Navbar";
import Title from "../../../../../Title";
import { useDebounce } from "../../../../../../Hooks/UseDebounce";

const CategoriasPage = () => {
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [currentCategoria, setCurrentCategoria] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        nombre: '',
        empresa_id: '',
        puesto_id: '',
        convenio_id: ''
    });
    const { enqueueSnackbar } = useSnackbar();

    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => {
        loadCategorias({ 
            page: page + 1, 
            rowsPerPage: rowsPerPage, 
            ...debouncedFilters 
        });
    }, [page, rowsPerPage, debouncedFilters]);
  
    const loadCategorias = async (params = {}) => {
        setLoading(true);
        try {
            const data = await fetchCategorias(params);
            if (data.data) {
                setCategorias(data.data);
                setTotal(parseInt(data.total) || data.data.length);
            } else {
                setCategorias(data || []);
                setTotal(data.length || 0);
            }
        } catch (error) {
            console.error("Error loading categorias:", error);
            enqueueSnackbar('Error al cargar categorías', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };
    
    const handleFilterChange = (column, value) => {
        setFilters(prev => ({ ...prev, [column]: value }));
    };

    useEffect(() => {
        setPage(0);
    }, [debouncedFilters]);

    const handleOpenCreate = () => {
        setCurrentCategoria(null);
        setFormOpen(true);
    };

    const handleOpenEdit = (categoria) => {
        setCurrentCategoria(categoria);
        setFormOpen(true);
    };

    const handleOpenDelete = (categoria) => {
        setCurrentCategoria(categoria);
        setDeleteOpen(true);
    };

    const getUser = () => {
        return localStorage.getItem("user") || "unknown";
    };

    const handleFormSubmit = async (formData) => {
        try {
            const user = getUser();
            const payload = { ...formData, username: user };

            if (currentCategoria) {
                await updateCategoria(currentCategoria.id_categoria, payload);
                enqueueSnackbar('Categoría actualizada correctamente', { variant: 'success' });
            } else {
                await createCategoria(payload);
                enqueueSnackbar('Categoría creada correctamente', { variant: 'success' });
            }
            setFormOpen(false);
            loadCategorias({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error saving categoria:", error);
            enqueueSnackbar('Error al guardar la categoría', { variant: 'error' });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!currentCategoria) return;
        try {
            await deleteCategoria(currentCategoria.id_categoria);
            enqueueSnackbar('Categoría eliminada correctamente', { variant: 'success' });
            setDeleteOpen(false);
            loadCategorias({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error deleting categoria:", error);
            enqueueSnackbar('Error al eliminar la categoría', { variant: 'error' });
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mb: 5 }}>
            <Navbar />
            <Title titulo="Gestión de Categorías" handleClose={() => window.history.back()} />
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} mb={2}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                        px: 3,
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
                        }
                    }}
                >
                    Nueva Categoría
                </Button>
            </Box>

            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <CategoriasTable
                    data={categorias}
                    loading={loading}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    total={total}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
            </Paper>

            <CategoriasForm
                open={formOpen}
                handleClose={() => setFormOpen(false)}
                handleSubmit={handleFormSubmit}
                initialData={currentCategoria}
            />

            <DeleteCategoriasDialog
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                handleConfirm={handleDeleteConfirm}
                itemName={currentCategoria?.nombre}
            />
        </Container>
    );
};

export default CategoriasPage;
